import React, { useRef, useMemo } from "react"
import PropTypes from "prop-types"
import { NavLink, useMatch, Link } from "react-router-dom"
import { routes } from "router/routes"
import UserMenu from "components/common/UserMenu"
import GuideNavigation from "./GuideNavigation"
import TripFiltersNavigation from "./TripFiltersNavigation"
import SearchTripsAntd from "./SearchTripsAntd"
import withDefaultErrorBoundary from "modules/errors/HOCs/withDefaultErrorBoundary"
import Logo from "components/common/Logo"
import Avatar from "components/common/Avatar"
import useEvent from "hooks/useEvent"
import { useDispatch, useSelector } from "react-redux"
import { updateWindowSize } from "modules/window/reducer"
import { isGuideSelector, updateUser } from "store/user"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { isProApp } from "utils/app-type"
import { getHostsByEnvironment } from "utils/host-urls"
import { Button, Flex, Switch, Layout, Space, Skeleton } from "antd"
import styles from "./style.module.css"

const { Header: AntdHeader, Content } = Layout

// Custom hook to determine logo navigation path
const useLogoNavigation = (loggedIn, isGuide, isGuideProfile, isProHost, isInIframe) => {
  const { PROTOCOL, CUSTOMER } = getHostsByEnvironment()

  return useMemo(() => {
    if (isInIframe) {
      return { logoPath: null, logoIsExternal: false }
    }

    if (loggedIn && isGuide && isGuideProfile) {
      return { logoPath: routes.guideRootPath(), logoIsExternal: false }
    }

    const shouldRedirectToCustomer = isProHost && isGuideProfile

    if (loggedIn && !isGuide) {
      if (shouldRedirectToCustomer) {
        return {
          logoPath: `${PROTOCOL}${CUSTOMER}/#${routes.tripsPath()}`,
          logoIsExternal: true
        }
      }
      return { logoPath: routes.tripsPath(), logoIsExternal: false }
    }

    if (!loggedIn) {
      if (shouldRedirectToCustomer) {
        return {
          logoPath: `${PROTOCOL}${CUSTOMER}/#${routes.rootPath()}`,
          logoIsExternal: true
        }
      }
      return { logoPath: routes.rootPath(), logoIsExternal: false }
    }

    return { logoPath: routes.rootPath(), logoIsExternal: false }
  }, [loggedIn, isGuide, isGuideProfile, isProHost, isInIframe, PROTOCOL, CUSTOMER])
}

// Component for rendering the header logo
const HeaderLogo = ({ isEmbedded = false, logoPath, logoIsExternal, isSportsman, customLogoUrl, customLogoLoading }) => {
  let logoElement
  if (customLogoUrl) {
    logoElement = <img src={customLogoUrl} alt="Business Logo" className={styles.customLogo} height={isEmbedded ? 40 : "auto"} />
  } else if (customLogoLoading) {
    logoElement = <Skeleton.Button active className={styles.customLogoSkeleton} />
  }  else {
    // Wide wordmark on desktop, compact icon on mobile (toggled via CSS) so the
    // logo doesn't crowd the auth buttons off the top row on small screens.
    const isSportswoman = !isSportsman
    logoElement = (
      <>
        <Logo alt="Logo" height={35} is_sportswoman={isSportswoman} wide className={styles.logoWide} />
        <Logo alt="Logo" height={35} is_sportswoman={isSportswoman} className={styles.logoCompact} />
      </>
    )
  }

  if (!logoPath) {
    return <div>{logoElement}</div>
  }

  if (logoIsExternal) {
    return (
      <a href={logoPath} title="Home" className={styles.logoWrapper}>
        {logoElement}
      </a>
    )
  }

  return (
    <NavLink to={logoPath} state={{ scrollToTop: true }} title="Home" className={styles.logoWrapper}>
      {logoElement}
    </NavLink>
  )
}

HeaderLogo.propTypes = {
  isEmbedded: PropTypes.bool,
  logoPath: PropTypes.string,
  logoIsExternal: PropTypes.bool.isRequired,
  isSportsman: PropTypes.bool.isRequired,
  customLogoUrl: PropTypes.string,
  customLogoLoading: PropTypes.bool,
}

// Component for rendering non-logged-in action buttons
const GuestActionButtons = ({ isProHost, isGuideProfile, isInIframe, t }) => {
  const { PROTOCOL, PRO_LANDING } = getHostsByEnvironment()

  const scrollToRequestDemo = () => {
    const demoRequestEl = document.querySelector("#demo-request")
    if (demoRequestEl) demoRequestEl.scrollIntoView({ behavior: "smooth" })
  }

  const showDemoButton = isProHost && !isGuideProfile && !isInIframe

  return (
    <>
      {!isProHost && (
        <a href={PROTOCOL + PRO_LANDING} target="_blank" rel="noopener noreferrer">
          <Button className={styles.headerBtn} type="primary">
            {t("global.become_a_guide")}
          </Button>
        </a>
      )}
      {showDemoButton && (
        <Button className={styles.headerBtn} type="link" style={{ fontFamily: "Epilogue" }} onClick={scrollToRequestDemo}>
          See a Demo
        </Button>
      )}
    </>
  )
}

GuestActionButtons.propTypes = {
  isProHost: PropTypes.bool.isRequired,
  isGuideProfile: PropTypes.bool.isRequired,
  isInIframe: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired
}

// Component for rendering sign in button
const SignInButton = ({ isProHost, signInPath, t }) => {
  const buttonElement = (
    <Button className={`${styles.headerBtn} px-m py-s`} type="primary">
      {t("global.sign_in")}
    </Button>
  )

  if (isProHost) {
    return (
      <a href={signInPath} className={styles.authButtons}>
        {buttonElement}
      </a>
    )
  }

  return (
    <Link to={signInPath} className={styles.authButtons}>
      {buttonElement}
    </Link>
  )
}

SignInButton.propTypes = {
  isProHost: PropTypes.bool.isRequired,
  signInPath: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired
}

// Component for rendering user menu section
const UserMenuSection = ({ isGuide, isGuideProfile, user, isSportsman, handleThemeChange }) => (
  <div className="my-2">
    <Flex align="center" gap={10}>
      <Switch checked={isSportsman} onChange={handleThemeChange} className={styles.themeToggle} />
      {isGuide && isGuideProfile ? <Avatar user={user} size={32} /> : <UserMenu />}
    </Flex>
  </div>
)

UserMenuSection.propTypes = {
  isGuide: PropTypes.bool.isRequired,
  isGuideProfile: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  isSportsman: PropTypes.bool.isRequired,
  handleThemeChange: PropTypes.func.isRequired
}

GuestHeader.propTypes = {
  noPadding: PropTypes.bool,
  fluid: PropTypes.bool,
  withNavigation: PropTypes.bool,
  withBotomBorder: PropTypes.bool,
  className: PropTypes.string,
  hideUserControls: PropTypes.bool,
  isEmbedded: PropTypes.bool
}

function GuestHeader({ noPadding = false, fluid = false, withNavigation = false, withBotomBorder: bottomBorder = false, className, hideUserControls = false, isEmbedded = false }) {
  const dispatch = useDispatch()
  const headerRef = useRef(null)
  const headerHeight = useSelector((store) => store.window)
  const { switchTheme, isSportsman } = useTheme()
  const isGuide = useSelector(isGuideSelector)
  const user = useSelector((store) => store.user)
  const { loggedIn } = useSelector((store) => store.auth)
  const { t } = useTranslation()

  const isProHost = isProApp()
  const isInIframe = window.self !== window.top
  const customBrandingLogoUrl = useSelector(
    (state) =>
      state?.experiences?.byGuide?.widgetLogoUrl || state?.experiences?.experienceById?.widgetLogoUrl || null
  )
  const customLogoLoading = useSelector(
    (state) => !!(isInIframe && !state?.experiences?.byGuide?.widgetLogoUrl && !state?.experiences?.experienceById?.widgetLogoUrl && (state?.experiences?.byGuide?.loading || state?.experiences?.experienceById?.loading))
  )
  
  const { PROTOCOL, CUSTOMER } = getHostsByEnvironment()
  const isClientLandingPage = !!useMatch(routes.rootPath()) && !isProHost
  const isGuideProfile = !!useMatch(routes.proGuideProfilePath())
  const isTripsPage = !!useMatch(routes.tripsPath())

  const { logoPath, logoIsExternal } = useLogoNavigation(loggedIn, isGuide, isGuideProfile, isProHost, isInIframe)

  const signInPath = useMemo(
    () => (isProHost ? `${PROTOCOL}${CUSTOMER}/#${routes.signInV2()}` : routes.signInV2()),
    [isProHost, PROTOCOL, CUSTOMER]
  )

  const navigationConfig = useMemo(
    () => ({
      showGuideNavigation: withNavigation && isGuide,
      showTripFiltersNavigation: (isTripsPage && !isGuide) || isClientLandingPage,
      showSearch: !isProHost || isGuideProfile,
      searchDisabled: isGuideProfile && ((!loggedIn && isProHost) || (loggedIn && isGuide))
    }),
    [withNavigation, isGuide, isTripsPage, isClientLandingPage, isProHost, isGuideProfile, loggedIn]
  )

  const withBotomBorder = useMemo(
    () => bottomBorder || navigationConfig.showGuideNavigation || navigationConfig.showTripFiltersNavigation,
    [bottomBorder, navigationConfig.showGuideNavigation, navigationConfig.showTripFiltersNavigation]
  )

  const setHeight = () => {
    if (!headerRef.current) return
    const height = headerRef.current.clientHeight
    const isHeightChanged = headerHeight !== height
    if (!isHeightChanged) return
    document.body.style.setProperty("--header-height", `${height}px`)
    dispatch(updateWindowSize({ headerHeight: height }))
  }

  useEvent("load", setHeight, true, window, true, true)
  useEvent("resize", setHeight, true, window)

  const classes = useMemo(() => {
    const classList = [styles.headerRoot, "sticky-top"]
    if (withBotomBorder) classList.push(styles.withBotomBorder)
    if (className) classList.push(className)
    return classList
  }, [withBotomBorder, className])

  const handleThemeChange = () => {
    switchTheme(isSportsman ? themes.sportswoman : themes.sportsman)
    dispatch(updateUser({ is_sportsman: !isSportsman }))
  }

  const shouldShowSignIn = !loggedIn && !isInIframe

  return (
    <div
      className={styles.stickyTopHeader}
      ref={headerRef}
      style={{ ...(isTripsPage && { maxHeight: 140 }), position: "sticky", top: 0, zIndex: 1000 }}
    >
      <AntdHeader className={`${classes.join(" ")}, layout-wrapper`} style={{ background: "#fff" }}>
        <Content style={{ width: "100%" }}>
          <Flex gap={10} justify="space-between" wrap="wrap" align="center">
            <Space direction="horizontal" size={32} align="center" className={styles.logoAndButtons}>
              <HeaderLogo isEmbedded={isEmbedded} logoPath={logoPath} logoIsExternal={logoIsExternal} isSportsman={isSportsman} customLogoUrl={isInIframe ? customBrandingLogoUrl : null} customLogoLoading={customLogoLoading}  />
              {!loggedIn && !hideUserControls && <GuestActionButtons isProHost={isProHost} isGuideProfile={isGuideProfile} isInIframe={isInIframe} t={t} />}
            </Space>

            <div className={styles.searchTripsWrap}>
              {navigationConfig.showSearch && (
                <SearchTripsAntd className={styles.searchTrips} disabled={navigationConfig.searchDisabled} />
              )}
            </div>

            {shouldShowSignIn && !hideUserControls && <SignInButton isProHost={isProHost} signInPath={signInPath} t={t} />}

            {loggedIn && !hideUserControls && (
              <UserMenuSection
                isGuide={isGuide}
                isGuideProfile={isGuideProfile}
                user={user}
                isSportsman={isSportsman}
                handleThemeChange={handleThemeChange}
              />
            )}
          </Flex>
        </Content>
      </AntdHeader>
      {navigationConfig.showGuideNavigation && <GuideNavigation />}
      {navigationConfig.showTripFiltersNavigation && (
        <TripFiltersNavigation visible={isClientLandingPage} fluid={!isClientLandingPage} />
      )}
    </div>
  )
}

export default withDefaultErrorBoundary(GuestHeader)
